import { TILE } from '../config/config';
import {
  ENTITY_LEGEND,
  T,
  charToTile,
  isBumpableTile,
  isHiddenTile,
  isOneWayTile,
  isSolidTile,
} from './tiles';

export interface EntitySpawn {
  type: string;
  /** World pixel coordinates of the tile's top-left corner. */
  x: number;
  y: number;
  tx: number;
  ty: number;
}

/**
 * Tile collision + query layer. Pure data: no rendering, no canvas, so it can be
 * exercised directly in unit tests.
 */
export class TileMap {
  readonly w: number;
  readonly h: number;
  readonly tiles: Uint8Array;
  /** Blocks that have been emptied keep a counter (multi-coin bricks). */
  readonly coinsLeft = new Map<number, number>();
  spawns: EntitySpawn[] = [];

  constructor(rows: string[]) {
    this.h = rows.length;
    this.w = rows.reduce((m, r) => Math.max(m, r.length), 0);
    this.tiles = new Uint8Array(this.w * this.h);
    for (let ty = 0; ty < this.h; ty++) {
      const row = rows[ty];
      for (let tx = 0; tx < this.w; tx++) {
        const ch = tx < row.length ? row[tx] : '.';
        const ent = ENTITY_LEGEND[ch];
        if (ent) {
          this.spawns.push({ type: ent, x: tx * TILE, y: ty * TILE, tx, ty });
          this.tiles[ty * this.w + tx] = T.EMPTY;
        } else {
          this.tiles[ty * this.w + tx] = charToTile(ch);
        }
      }
    }
  }

  get pixelW(): number {
    return this.w * TILE;
  }

  get pixelH(): number {
    return this.h * TILE;
  }

  inBounds(tx: number, ty: number): boolean {
    return tx >= 0 && tx < this.w && ty >= 0 && ty < this.h;
  }

  /**
   * Tile lookup with out-of-bounds rules:
   * - left / right of the level act as walls (you cannot leave the level sideways)
   * - above and below the level are empty (you may jump off-screen, and fall to death)
   */
  get(tx: number, ty: number): number {
    if (tx < 0 || tx >= this.w) return T.ROCK;
    if (ty < 0 || ty >= this.h) return T.EMPTY;
    return this.tiles[ty * this.w + tx];
  }

  set(tx: number, ty: number, id: number): void {
    if (!this.inBounds(tx, ty)) return;
    this.tiles[ty * this.w + tx] = id;
  }

  solid(tx: number, ty: number): boolean {
    return isSolidTile(this.get(tx, ty));
  }

  oneWay(tx: number, ty: number): boolean {
    return isOneWayTile(this.get(tx, ty));
  }

  bumpable(tx: number, ty: number): boolean {
    return isBumpableTile(this.get(tx, ty));
  }

  hidden(tx: number, ty: number): boolean {
    return isHiddenTile(this.get(tx, ty));
  }

  /** Solid tile at a world pixel position. */
  solidAtPixel(x: number, y: number): boolean {
    return this.solid(Math.floor(x / TILE), Math.floor(y / TILE));
  }

  /** True when the rectangle overlaps any fully solid tile. */
  rectHitsSolid(x: number, y: number, w: number, h: number): boolean {
    const x0 = Math.floor(x / TILE);
    const x1 = Math.floor((x + w - 0.001) / TILE);
    const y0 = Math.floor(y / TILE);
    const y1 = Math.floor((y + h - 0.001) / TILE);
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        if (this.solid(tx, ty)) return true;
      }
    }
    return false;
  }

  key(tx: number, ty: number): number {
    return ty * this.w + tx;
  }

  /** Snapshot for restarts (tiles mutate as blocks are hit). */
  snapshot(): Uint8Array {
    return this.tiles.slice();
  }

  restore(snap: Uint8Array): void {
    this.tiles.set(snap);
    this.coinsLeft.clear();
  }
}
